'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TimelineEvent {
  year: number;
  quarter: string;
  title: string;
  description: string;
  phase: number;
  status: 'completed' | 'in-progress' | 'planned';
}

interface TimelineVisualizationProps {
  events: TimelineEvent[];
}

export default function TimelineVisualization({ events }: TimelineVisualizationProps) {
  const [selectedPhase, setSelectedPhase] = useState<number | null>(null);

  const filteredEvents = selectedPhase
    ? events.filter((event) => event.phase === selectedPhase)
    : events;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-success';
      case 'in-progress':
        return 'bg-warning';
      case 'planned':
        return 'bg-muted-foreground';
      default:
        return 'bg-muted-foreground';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return 'CheckCircleIcon';
      case 'in-progress':
        return 'ClockIcon';
      case 'planned':
        return 'CalendarIcon';
      default:
        return 'CalendarIcon';
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-institutional p-4 sm:p-6 border border-border">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2 sm:mb-4 md:mb-6">
        <h2 className="font-headline text-2xl font-semibold text-foreground mb-2 md:mb-0">
          Implementation Timeline
        </h2>
        <div className="flex items-center space-x-1 sm:space-x-2">
          <button
            onClick={() => setSelectedPhase(null)}
            className={`px-4 py-2 font-body text-sm rounded-md transition-colors duration-300 ${
              selectedPhase === null
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            All Phases
          </button>
          {[1, 2, 3].map((phase) => (
            <button
              key={phase}
              onClick={() => setSelectedPhase(phase)}
              className={`px-4 py-2 font-body text-sm rounded-md transition-colors duration-300 ${
                selectedPhase === phase
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Phase {phase}
            </button>
          ))}
        </div>
      </div>

      <div className="relative">
        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border" />

        <div className="space-y-2 sm:space-y-4">
          {filteredEvents.map((event, index) => (
            <div key={index} className="relative pl-20">
              <div
                className={`absolute left-6 w-4 h-4 rounded-full ${getStatusColor(
                  event.status
                )} border-4 border-background`}
              />

              <div className="bg-muted rounded-lg p-2 sm:p-6 hover:shadow-md transition-shadow duration-300">
                <div className="flex items-start justify-between mb-2 sm:mb-4">
                  <div className="flex items-center space-x-1 sm:space-x-3">
                    <div className="hidden sm:block">
                      <Icon
                        name={getStatusIcon(event.status) as any}
                        size={20}
                        variant="solid"
                        className={`${
                          event.status === 'completed'
                            ? 'text-success'
                            : event.status === 'in-progress'
                              ? 'text-warning'
                              : 'text-muted-foreground'
                        }`}
                      />
                    </div>
                    <div>
                      <h3 className="font-headline text-lg font-semibold text-foreground">
                        {event.title}
                      </h3>
                      <p className="font-body text-sm font-bold text-muted-foreground">
                        {event.quarter} {event.year} • Phase {event.phase}
                      </p>
                    </div>
                  </div>
                </div>
                <p className="font-body text-sm text-text-secondary leading-relaxed">
                  {event.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
